import {
  addPercentage,
  formatNumberWithPrecision,
} from "@utils/product/productFormatters";
import { DesignOptions, SelectionType } from "../types";
import { removeFiltersPayload } from "@redux/slices/searchFilters";
import { SearchFilterTypeEnum } from "../utils";

export const isPreviouslySelected = (
  payloadName: Array<any>,
  selectedItems: any,
  type: DesignOptions,
  sectionName: string,
  selectionType: SelectionType
) => {
  if (!selectedItems || !payloadName) return null;

  switch (type) {
    case DesignOptions.text_list:
      if (selectionType === SelectionType.range)
        return [
          selectedItems?.[sectionName]?.[payloadName[0]],
          selectedItems?.[sectionName]?.[payloadName[1]],
        ];

      if (selectedItems[sectionName]?.hasOwnProperty(payloadName[0]))
        return selectedItems[sectionName][payloadName[0]];
      break;
    case DesignOptions.image_text_list:
      if (selectedItems[sectionName]?.hasOwnProperty(payloadName[0]))
        return selectedItems[sectionName][payloadName[0]];
      break;
    case DesignOptions.checkbox_list:
      if (selectedItems[sectionName]?.hasOwnProperty(payloadName[0]))
        return selectedItems[sectionName][payloadName[0]];
      break;
    case DesignOptions.round_checkbox_list:
      if (selectedItems[sectionName]?.hasOwnProperty(payloadName[0]))
        return selectedItems[sectionName][payloadName[0]];
      break;
    case DesignOptions.dropdown:
      if (selectedItems[sectionName]?.hasOwnProperty(payloadName[0]))
        return selectedItems[sectionName][payloadName[0]];
      break;
    case DesignOptions.range_list:
      return [
        selectedItems?.[sectionName]?.[payloadName[0]],
        selectedItems?.[sectionName]?.[payloadName[1]],
      ];
    case DesignOptions.range:
      return [
        selectedItems?.[sectionName]?.[payloadName[0]],
        selectedItems?.[sectionName]?.[payloadName[1]],
      ];
    case DesignOptions.range_slider:
      return [
        selectedItems?.[sectionName]?.[payloadName[0]],
        selectedItems?.[sectionName]?.[payloadName[1]],
      ];
    default:
      return null;
  }
};

export const checkboxPreviouslySelected = (
  payloadName: any[] | any,
  selectedItems: any,
  sectionName: string | undefined,
  options: any[]
) => {
  if (!selectedItems || !payloadName) return null;
  const values = [];
  if (
    selectedItems?.[sectionName as keyof typeof SearchFilterTypeEnum]?.[
      payloadName?.[0]
    ]
  ) {
    values.push(
      selectedItems[sectionName as keyof typeof SearchFilterTypeEnum][
        payloadName[0]
      ]
    );
  }
  options.forEach((option) => {
    if (option?.payload_name?.[0]) {
      if (
        selectedItems?.[sectionName as keyof typeof SearchFilterTypeEnum]?.[
          option?.payload_name?.[0]
        ]
      )
        values.push(option?.payload_name?.[0]);
    }
  });
  return values?.flat() || [];
};

export const handleBlurMinMax = (
  isMin: boolean,
  minAndMax: any,
  setMinAndMax: any,
  isPrice?: boolean,
  subSectionName?: string,
  dispatch?: any,
  sectionName?: any,
  payloadName?: any,
  isParcelCarat: boolean = false,
  enableFourDecimalCarat: boolean = false
) => {
  const formatValue = (value: string, misPrice?: boolean) => {
    value = value.replace(/,/g, "");

    if (value.startsWith(".")) {
      value = `0${value}`;
    }

    if (!isNaN(parseFloat(value))) {
      return misPrice
        ? parseFloat(value).toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : formatNumberWithPrecision(value, {
            precision: isParcelCarat ? (enableFourDecimalCarat ? 4 : 3) : 2,
          });
    }

    return value;
  };

  if (isMin) {
    let value = minAndMax.min ? minAndMax.min.toString() : "";
    value = formatValue(value, isPrice);
    if (subSectionName === "Discount") {
      if (value === "-" || !value) {
        value = "";
        dispatch(
          removeFiltersPayload({
            sectionName,
            payloadName: payloadName.slice(0, 1),
          })
        );
      } else if (value && value !== "-") {
        value = addPercentage(value);
      }
    }
    if (value || value === "") {
      setMinAndMax((prev: { min: string | null; max: string | null }) => ({
        ...prev,
        min: value,
      }));
    }
  } else {
    let value = minAndMax.max ? minAndMax.max.toString() : "";
    value = formatValue(value, isPrice);
    if (subSectionName === "Discount") {
      if (value === "-" || !value) {
        value = "";
        dispatch(
          removeFiltersPayload({
            sectionName,
            payloadName: payloadName.slice(1),
          })
        );
      } else if (value && value !== "-") {
        value = addPercentage(value);
      }
    }
    if (value || value === "") {
      setMinAndMax((prev: { min: string | null; max: string | null }) => ({
        ...prev,
        max: value,
      }));
    }
  }
};

export const handleBlurMinMaxRef = (
  isMin: boolean,
  minAndMaxRef: React.MutableRefObject<{
    min: string | null;
    max: string | null;
  }>,
  setInputValue?: (val: string) => void,
  mIsPrice?: boolean,
  subSectionName?: string,
  dispatch?: any,
  sectionName?: any,
  payloadName?: any
) => {
  const formatValue = (value: string, isPrice?: boolean) => {
    value = value.replace(/,/g, "");

    if (value.startsWith(".")) {
      value = `0${value}`;
    }

    if (!isNaN(parseFloat(value))) {
      return isPrice
        ? parseFloat(value).toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : formatNumberWithPrecision(value, {
            precision: 2,
          });
    }

    return value;
  };

  const updateValue = (key: "min" | "max", rawValue: string) => {
    let value = rawValue ? rawValue.toString() : "";
    value = formatValue(value, mIsPrice);

    if (subSectionName === "Discount") {
      if (value === "-" || !value) {
        value = "";
        dispatch?.(
          removeFiltersPayload({
            sectionName,
            payloadName:
              key === "min" ? payloadName?.slice(0, 1) : payloadName?.slice(1),
          })
        );
      } else {
        value = addPercentage(value);
      }
    }

    minAndMaxRef.current[key] = value;

    if (setInputValue) {
      setInputValue(value);
    }
  };

  if (isMin) {
    updateValue("min", minAndMaxRef.current.min ?? "");
  } else {
    updateValue("max", minAndMaxRef.current.max ?? "");
  }
};

export const commaFormattedValue = (text: string, isPrice: boolean) => {
  if (typeof text === "number" && isNaN(text)) return;
  const pricePattern = /^\d{1,3}(,\d{3})*(\.\d{0,3})?|\d+(\.\d{0,3})?$/;
  const nonPricePattern = /^\d+(\.\d{0,3})?$/;

  if (isPrice && pricePattern.test(text)) {
    let formattedValue = text.toString().replace(/[^0-9.]/g, "");

    const firstDecimalIndex = formattedValue.indexOf(".");

    if (firstDecimalIndex !== -1) {
      let firsPart = formattedValue.slice(0, firstDecimalIndex + 1);
      let secondPart = formattedValue
        .slice(firstDecimalIndex + 1)
        .replace(/\./g, "");
      formattedValue = firsPart + secondPart;
    }

    if (formattedValue[0] === ".") {
      formattedValue = "0" + formattedValue;
    }

    if (
      firstDecimalIndex > -1 &&
      firstDecimalIndex === formattedValue.length - 1
    ) {
      return formattedValue;
    }

    if (firstDecimalIndex > -1) {
      formattedValue = formattedValue.slice(0, firstDecimalIndex + 3);
    }

    const formattedWithCommas = new Intl.NumberFormat("en-US").format(
      parseFloat(formattedValue)
    );
    return formattedWithCommas;
  } else if (nonPricePattern.test(text)) {
    return text;
  }
  return text;
};

export const removeCommaFormatted = (_value: string) => {
  if (!_value || typeof _value !== "string") return "0";
  const [wholePart, decimalPart] = _value.split(".");
  const cleanWholePart = wholePart.replace(/,/g, "");
  return decimalPart ? `${cleanWholePart}.${decimalPart}` : cleanWholePart;
};
